import { FormEvent, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../../../api";
import { canAccessPropertySetup, getAuthErrorMessage } from "../auth";
import { gradientBrand, passtrackColors } from "../theme";
import { useAuth } from "../routing/AuthContext";
import {
  EntryLogo,
  EntryShell,
  entryMenuCardSx,
} from "../routing/entryShell";
import { ROUTES } from "../routing/paths";

const primaryButtonSx = {
  py: 1.25,
  fontWeight: 700,
  backgroundImage: gradientBrand,
  "&:hover": {
    backgroundImage: gradientBrand,
    filter: "brightness(0.95)",
  },
} as const;

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (event: FormEvent) => {
    event.preventDefault();
    if (!email.trim() || !password) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const loginResult = await api.loadAuthSession(email, password);

      if (!canAccessPropertySetup(loginResult.user.role)) {
        login(loginResult);
        navigate(ROUTES.unauthorized, { replace: true });
        return;
      }

      login(loginResult);

      if (loginResult.properties.length === 0) {
        navigate(ROUTES.setupNew, { replace: true });
        return;
      }

      navigate(ROUTES.menu, { replace: true });
    } catch (loginError) {
      setError(getAuthErrorMessage(loginError));
    } finally {
      setLoading(false);
    }
  };

  return (
    <EntryShell>
      <Card
        variant="outlined"
        component="form"
        onSubmit={handleLogin}
        sx={{ ...entryMenuCardSx, maxWidth: 420 }}
      >
        <EntryLogo />
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: passtrackColors.blue,
            mb: 1.5,
          }}
        >
          Control de acceso
        </Typography>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: 26, sm: 28 },
            lineHeight: 1.2,
            color: passtrackColors.ink,
            mb: 1,
          }}
        >
          Setup de Propiedad
        </Typography>
        <Typography
          sx={{
            fontSize: 14,
            lineHeight: 1.5,
            color: passtrackColors.textMuted,
            mb: 3,
          }}
        >
          Inicia sesión para configurar una propiedad nueva o editar una
          existente.
        </Typography>

        {error ? (
          <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>
            {error}
          </Alert>
        ) : null}

        <TextField
          fullWidth
          size="small"
          label="Correo electrónico"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="tu@correo.com"
          autoComplete="email"
          disabled={loading}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          size="small"
          label="Contraseña"
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          placeholder="••••••••"
          autoComplete="current-password"
          disabled={loading}
          sx={{ mb: 2 }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    onMouseDown={(event) => event.preventDefault()}
                    edge="end"
                    aria-label={
                      showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
                    }
                    disabled={loading}
                    size="small"
                  >
                    {showPassword ? (
                      <EyeOff size={18} strokeWidth={2} />
                    ) : (
                      <Eye size={18} strokeWidth={2} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={!email.trim() || !password || loading}
          sx={primaryButtonSx}
        >
          {loading ? (
            <CircularProgress size={22} sx={{ color: "white" }} />
          ) : (
            "Iniciar sesión"
          )}
        </Button>
      </Card>
    </EntryShell>
  );
}
